import React from 'react';
import styled, { css } from 'styled-components';
import { Box, type BoxProps } from '../box';
import { spacing } from '../../style';

type GridTemplateValue = number | string;
type GridSpacingValue = keyof typeof spacing;

export interface GridProps extends BoxProps {
  /**
   * Grid template columns.
   * Use a number for equal-width tracks or pass a raw CSS grid-template-columns value.
   */
  cols?: GridTemplateValue;
  /**
   * Grid template rows.
   * Use a number for repeated auto-sized rows or pass a raw CSS grid-template-rows value.
   */
  rows?: GridTemplateValue;
  /**
   * Creates an auto-fitting responsive grid using
   * `repeat(auto-fit, minmax(minChildWidth, 1fr))`.
   * Takes precedence over numeric `cols`.
   */
  minChildWidth?: string;
  /**
   * Controls item placement direction for the auto-placement algorithm.
   */
  autoFlow?: React.CSSProperties['gridAutoFlow'];
  /**
   * Sets the size of implicitly created rows.
   */
  autoRows?: React.CSSProperties['gridAutoRows'];
  /**
   * Sets the size of implicitly created columns.
   */
  autoCols?: React.CSSProperties['gridAutoColumns'];
  /**
   * Sets horizontal spacing between columns using the design-system spacing scale.
   */
  columnGap?: GridSpacingValue;
}

function resolveTemplateValue(value: GridTemplateValue | undefined, axis: 'columns' | 'rows') {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === 'number') {
    if (axis === 'columns') {
      return `repeat(${value}, minmax(0, 1fr))`;
    }

    return `repeat(${value}, minmax(0, auto))`;
  }

  return value;
}

const StyledGrid = styled(Box).withConfig({
  shouldForwardProp: prop =>
    !new Set(['cols', 'rows', 'minChildWidth', 'autoFlow', 'autoRows', 'autoCols', 'columnGap']).has(prop),
})<GridProps>`
  ${props => {
    const gridTemplateColumns = props.minChildWidth
      ? `repeat(auto-fit, minmax(${props.minChildWidth}, 1fr))`
      : resolveTemplateValue(props.cols, 'columns');
    const gridTemplateRows = resolveTemplateValue(props.rows, 'rows');

    return css`
      ${gridTemplateColumns &&
      css`
        grid-template-columns: ${gridTemplateColumns};
      `}

      ${gridTemplateRows &&
      css`
        grid-template-rows: ${gridTemplateRows};
      `}

      ${props.autoFlow &&
      css`
        grid-auto-flow: ${props.autoFlow};
      `}

      ${props.autoRows &&
      css`
        grid-auto-rows: ${props.autoRows};
      `}

      ${props.autoCols &&
      css`
        grid-auto-columns: ${props.autoCols};
      `}

      ${props.columnGap &&
      css`
        column-gap: ${spacing[props.columnGap]};
      `}
    `;
  }}
`;

const Grid = ({ display = 'grid', ...rest }: GridProps) => <StyledGrid display={display} {...rest} />;

export default Grid;
